import React from "react";
import { Box, Card, Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined";
import { PALETTE } from "../types";

const Step: React.FC<{ n: number; icon: React.ReactNode; title: string; body: string }> = ({ n, icon, title, body }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
    <Box sx={{ position: "relative", flexShrink: 0 }}>
      <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: PALETTE.info.bg, color: PALETTE.navy, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 18 } }}>
        {icon}
      </Box>
      <Box sx={{ position: "absolute", top: -4, right: -4, width: 16, height: 16, borderRadius: "50%", bgcolor: PALETTE.navy, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800 }}>{n}</Box>
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: PALETTE.ink }}>{title}</Typography>
      <Typography sx={{ fontSize: 11.5, color: PALETTE.slate, mt: 0.25 }}>{body}</Typography>
    </Box>
  </Stack>
);

type Props = { filter: string; onSeeApproved?: () => void };

const MasteryApprovalEmptyState: React.FC<Props> = ({ filter }) => {
  const isPending = filter === "pending";
  const title = isPending ? "You're all caught up" : `No ${filter} reviews`;
  const body = isPending
    ? "There are no mastery decisions waiting for supervisor review."
    : `Nothing has been ${filter} yet in the current scope.`;
  return (
    <Stack spacing={2}>
      <Card sx={{ p: { xs: 3, md: 5 }, borderRadius: 2.5, border: `1px solid ${PALETTE.border}`, boxShadow: "none", textAlign: "center" }}>
        <Box sx={{ width: 56, height: 56, mx: "auto", borderRadius: "50%", bgcolor: PALETTE.approved.bg, color: PALETTE.approved.accent, display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5, "& svg": { fontSize: 28 } }}>
          <CheckCircleOutlineIcon />
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: PALETTE.ink, mb: 0.5 }}>{title}</Typography>
        <Typography sx={{ fontSize: 13.5, color: PALETTE.slate, maxWidth: 520, mx: "auto" }}>{body}</Typography>
        {isPending && (
          <Typography sx={{ fontSize: 12.5, color: PALETTE.slate, mt: 1.5, maxWidth: 520, mx: "auto" }}>
            New requests will appear here automatically when a target meets criteria on a phase that requires supervisor approval.
          </Typography>
        )}
      </Card>
      {isPending && (
        <Card sx={{ p: { xs: 2.25, md: 3 }, borderRadius: 2.5, border: `1px solid ${PALETTE.border}`, boxShadow: "none" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: PALETTE.slate, mb: 1.5 }}>
            How mastery approvals work
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Step n={1} icon={<RuleOutlinedIcon />} title="Target meets criteria" body="Data captured in session reaches the mastery threshold on a phase that requires review." />
            <Step n={2} icon={<PendingActionsIcon />} title="Supervisor review created" body="A pending approval appears here with the current phase and proposed destination." />
            <Step n={3} icon={<GavelOutlinedIcon />} title="Approve or reject" body="You review the criteria and decide whether to apply the transition or return it for more data." />
            <Step n={4} icon={<RepeatOutlinedIcon />} title="Workflow applies the outcome" body="On approval, the configured next phase or target status is applied automatically." />
          </Stack>
        </Card>
      )}
    </Stack>
  );
};

export default MasteryApprovalEmptyState;
